import { useEffect, useMemo, useState, useCallback, useRef } from "react";import api from "@/lib/api";
import { API } from "@/lib/api";
import { statusBadgeClass, fmtDate, waLink, formatApiError } from "@/lib/utils";
import { Plus, Search, Download, Upload, Pencil, Trash2, MessageCircle, X, Save, Scissors, CheckCircle2, Eye, Receipt, ScrollText, ClipboardList, Inbox, Share2, Copy, Loader2, IndianRupee, MoreVertical, RotateCcw, Users2, History } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import ImportMapper from "@/components/ImportMapper";
import InvoiceModal from "@/components/InvoiceModal";
import ProposalModal from "@/components/ProposalModal";
import FinancialCenterModal from "@/components/FinancialCenterModal";
import EditFinalCrewDrawer from "@/components/EditFinalCrewDrawer";
import DeliverablesPicker from "@/components/DeliverablesPicker";
import PageHero from "@/components/PageHero";
import { useDraftAutosave, loadDraft, clearDraft, useBeforeUnloadWarn, fmtDraftAgo } from "@/lib/draftAutosave";

// iter98 — Compact icon-only tracker actions for the Bookings row.
// Two 28×28 buttons matching the existing row aesthetic:
//   • Share2 → opens wa.me with pre-filled tracker link
//   • Copy   → copies /track/{token} to clipboard
// Uses `GET /team/tracker-link` which reuses an existing contract token
// or creates a minimal stub if none exists yet.
import { toast as _toast } from "sonner";
function BookingTrackerButton({ clientName, place, whatsapp, testId }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  // iter121 — Managers must never see tracker share/copy affordances.
  if (user?.role === "manager") return null;

  async function loadLink() {
    const params = new URLSearchParams({ client_name: clientName || "", place: place || "" });
    const { data } = await api.get(`/team/tracker-link?${params.toString()}`);
    return `${window.location.origin}${data.path}`;
  }

  const share = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const url = await loadLink();
      const number = String(whatsapp || "").replace(/\D/g, "");
      const msg = encodeURIComponent(`Hi ${clientName}, here's your live Kahani project tracker — payments, milestones and deliverables:\n${url}`);
      const target = number ? `https://wa.me/${number}?text=${msg}` : `https://wa.me/?text=${msg}`;
      window.open(target, "_blank", "noopener,noreferrer");
      _toast.success(number ? "Tracker sent to WhatsApp" : "Pick a contact to share");
    } catch (e) {
      _toast.error(e.response?.data?.detail || "Couldn't build tracker link");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      const url = await loadLink();
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      _toast.success("Tracker link copied");
    } catch (e) {
      _toast.error(e.response?.data?.detail || e.message || "Couldn't copy");
    }
  };

  return (
    <>
      <button
        onClick={share}
        disabled={busy}
        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
        title="Share live tracker on WhatsApp"
        data-testid={`${testId}-wa`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" strokeWidth={1.6} />}
      </button>
      <button
        onClick={copy}
        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-stone-700 hover:bg-stone-100"
        title="Copy tracker link"
        data-testid={`${testId}-copy`}
      >
        {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-600" strokeWidth={2} /> : <Copy className="w-4 h-4" strokeWidth={1.6} />}
      </button>
    </>
  );
}

// iter144 — Proposal button + live lifecycle status chip.
// Renders the classic ScrollText icon plus a color-coded pill showing
// the derived proposal state (Draft / Sent / Viewed / Accepted /
// Rejected / Expired) so the studio can see at a glance which
// bookings need follow-up without opening the modal.
const PROPOSAL_STATUS_MAP = {
  draft:    { label: "Draft",    cls: "bg-stone-100 text-stone-700 border-stone-200" },
  sent:     { label: "Sent",     cls: "bg-sky-50 text-sky-700 border-sky-200" },
  viewed:   { label: "Viewed",   cls: "bg-amber-50 text-amber-800 border-amber-200" },
  accepted: { label: "Accepted", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  rejected: { label: "Declined", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  expired:  { label: "Expired",  cls: "bg-stone-100 text-stone-500 border-stone-200" },
};
function deriveProposalStatus(quote) {
  if (!quote || !quote.token) return null;
  const raw = (quote.status || "sent").toLowerCase();
  if (raw === "accepted") return "accepted";
  if (raw === "rejected") return "rejected";
  const gen = quote.generated_at;
  const days = Number(quote.validity_days || 0);
  if (gen && days > 0) {
    try {
      const genDt = new Date(gen);
      const exp = new Date(genDt.getTime() + days * 86400000);
      if (Date.now() > exp.getTime()) return "expired";
    } catch { /* ignore */ }
  }
  if (Number(quote.view_count || 0) > 0) return "viewed";
  if (raw === "draft") return "draft";
  return "sent";
}
// iter156 — Row kebab menu (Edit / Duplicate / Delete). Kept minimal —
// other actions (Invoice / Tracker / PP-link / Financial Center) remain
// as icon buttons since coordinators use them daily. This menu holds
// the destructive / structural actions.
function BookingRowMenu({ booking, canWrite, isAdmin, onEdit, onDuplicate, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  if (!canWrite && !isAdmin) return null;
  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-stone-500 hover:bg-stone-100"
        title="More actions"
        aria-label="More actions"
        data-testid={`booking-row-menu-${booking.id}`}
      >
        <MoreVertical className="w-4 h-4" strokeWidth={1.6} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-20 bg-white border border-stone-200 rounded-lg shadow-lg py-1 min-w-[150px] text-xs"
          role="menu"
          data-testid={`booking-row-menu-panel-${booking.id}`}
        >
          {canWrite && (
            <button
              type="button"
              onClick={() => { setOpen(false); onDuplicate(booking.id); }}
              className="w-full text-left px-3 py-1.5 hover:bg-stone-50 inline-flex items-center gap-2 text-stone-700"
              data-testid={`booking-action-duplicate-${booking.id}`}
            >
              <Copy className="w-3.5 h-3.5" strokeWidth={2} /> Duplicate Event
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={() => { setOpen(false); onDelete(booking.id); }}
              className="w-full text-left px-3 py-1.5 hover:bg-red-50 inline-flex items-center gap-2 text-red-700"
              data-testid={`booking-action-delete-${booking.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={2} /> Delete Event
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ProposalStatusButton({ booking, onOpen }) {
  const status = deriveProposalStatus(booking?.quote);
  const cfg = status ? PROPOSAL_STATUS_MAP[status] : null;
  return (
    <button
      onClick={onOpen}
      className="inline-flex items-center gap-1.5 rounded-md text-amber-800 hover:bg-amber-50 px-1.5 h-7"
      title={cfg ? `Proposal · ${cfg.label}` : "Create Proposal"}
      data-testid={`booking-quote-${booking.id}`}
    >
      <ScrollText className="w-4 h-4" strokeWidth={1.6} />
      {cfg && (
        <span
          className={`px-1.5 py-[1px] rounded-full text-[9px] uppercase tracking-widest font-semibold border ${cfg.cls}`}
          data-testid={`booking-quote-status-${booking.id}`}
        >
          {cfg.label}
        </span>
      )}
    </button>
  );
}

const emptyForm = {
  status: "WAITING",
  client_name: "",
  contact: "",
  day1: "",
  day2: "",
  day3: "",
  day4: "",
  day5: "",
  couple_name: "",
  location: "",
  event: "",
  reference: "",
  last_updates: "",
  management: "",
  // iter30 — per-day crew requirements matrix.
  crew_requirements: {},
  // iter33 — per-day event type override (e.g., Haldi, Sangeet).
  day_event_types: {},
  // iter36 — per-day side (Bride Side / Groom Side / Both Side).
  day_sides: {},
  // iter36 — bulleted deliverables list (invoice PDF renders these).
  deliverables: [],
  // iter39 — per-day split toggle + independent bride/groom crew grids.
  day_splits: {},
  day_bride_crew: {},
  day_groom_crew: {},
  // iter39 — unbounded event days (Day 6 onwards). Each entry:
  //   { date, event_type, side, split, crew, bride_crew, groom_crew }
  extra_days: [],
  // iter137 — Split Payment. Party names auto-default to "Bride Side" /
  // "Groom Side" 50/50 when the toggle flips on. Amounts must sum to
  // `booking.quote.grand_total` (validated server-side).
  payment_split: { enabled: false, parties: [] },
};

const EVENT_TYPE_PRESETS = [
  "Haldi",
  "Mehendi",
  "Sangeet",
  "Wedding",
  "Reception",
  "Engagement",
  "Pre-Wedding Shoot",
];

const SIDE_OPTIONS = ["Both Side", "Bride Side", "Groom Side"];

const CREW_ROLES = [
  { key: "cinematographer",  label: "Cinematographers",           rate: 20000 },
  { key: "candid",           label: "Candid Photographers",       rate: 20000 },
  { key: "traditional",      label: "Traditional Photographers",  rate: 17000 },
  { key: "drone",            label: "Drone Operators",            rate: 17000 },
  { key: "family_portrait",  label: "Family Portrait Photographers", rate: 20000 },
  { key: "reels_creator",    label: "Reels Creators",             rate: 17000 },
  { key: "same_day_editor",  label: "Same Day Editors",           rate: 15000 },
  { key: "traditional_videographer", label: "Traditional Videographers", rate: 17000 },
];

const STATUS = ["CONFIRM", "WAITING", "CANCEL"];

// iter161 — Latest event date across day1..day5 + extra_days. Empty when
// no dates are set (booking treated as "not-yet-scheduled" → stays Active).
// ISO yyyy-mm-dd sorts lexicographically = chronologically.
function latestBookingDate(b) {
  const dates = [b?.day1, b?.day2, b?.day3, b?.day4, b?.day5,
    ...((b?.extra_days || []).map((d) => d?.date))].filter(Boolean);
  if (!dates.length) return "";
  return dates.slice().sort().at(-1);
}

export default function Bookings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isViewer = user?.role === "viewer";
  const canWrite = user?.role !== "viewer"; // iter120: admin + manager + coordinator all get write access
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  // iter143 — Draft autosave state.
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [pendingRestore, setPendingRestore] = useState(null); // draft object or null
  const draftScope = "booking";
  const draftId = editing?.id || "new";

  useDraftAutosave({
    scope: draftScope,
    id: draftId,
    value: form,
    enabled: showForm && dirty,
    onDirty: (ts) => setDraftSavedAt(ts),
  });
  useBeforeUnloadWarn(dirty, showForm);
  const [err, setErr] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [invoiceFor, setInvoiceFor] = useState(null);
  const [finalCrewFor, setFinalCrewFor] = useState(null);
  const [financialFor, setFinancialFor] = useState(null);
  // iter62 — top-level split between active bookings and public inquiries.
  // iter161 — extended with a "past" tab so operators can still search
  // completed / cancelled / past-date shoots without polluting Active.
  const [activeTab, setActiveTab] = useState("active"); // "active" | "inquiries" | "past"
  const [quoteFor, setQuoteFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings");
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // iter43/45 — Auto-fill deliverables rule engine.
  //
  //   Global:
  //     sum(Cinematographers across all days) ≥ 2       → add "Teaser Video" + "Trailer Video"
  //     ANY photographer (Candid OR Traditional) ≥ 1    → add ONE "Best Photos" (studio-wide)
  //   Per-day: for each day with `event_type` set:
  //     Cinematographers ≥ 1                            → add "{Event} FF"
  //     Candid OR Traditional Photographers ≥ 1         → add "{Event} All Photos"
  //
  //   Only NEWLY-recommended items are appended each render — items dropped
  //   from the recommendation set (because the crew was zeroed out again) do
  //   NOT get re-added, and any 'X'-removed item stays removed for the rest
  //   of the edit session. Users can always toggle presets back on manually.
  const lastRecommendedRef = useRef(new Set());
  // Reset the "already recommended" memory whenever we open a fresh form
  // (new booking OR switching to a different existing booking).
  useEffect(() => {
    if (showForm) lastRecommendedRef.current = new Set();
  }, [showForm, editing?.id]);
  useEffect(() => {
    if (!showForm) return;
    // Build a normalised list of slots (day1..5 + extra_days).
    const slots = [];
    for (const k of ["day1", "day2", "day3", "day4", "day5"]) {
      if (!form[k]) continue;
      slots.push({
        event: (form.day_event_types?.[k] || "").trim(),
        split: !!form.day_splits?.[k],
        crew: form.crew_requirements?.[k] || {},
        bride: form.day_bride_crew?.[k] || {},
        groom: form.day_groom_crew?.[k] || {},
      });
    }
    for (const ed of (form.extra_days || [])) {
      if (!ed?.date) continue;
      slots.push({
        event: (ed.event_type || "").trim(),
        split: !!ed.split,
        crew: ed.crew || {},
        bride: ed.bride_crew || {},
        groom: ed.groom_crew || {},
      });
    }
    const countRole = (s, key) => s.split
      ? (Number(s.bride?.[key]) || 0) + (Number(s.groom?.[key]) || 0)
      : (Number(s.crew?.[key]) || 0);
    // iter114 — Split-aware helpers: when "SPLIT CREW FOR PARALLEL SIDES?"
    // is ON, check each side independently so we recommend
    // "Bride Side [Event] FF" + "Groom Side [Event] FF" only when both
    // sides actually have the relevant crew. Falls back to the joint
    // recommendation ("[Event] FF") when split is off.
    const brideCount = (s, key) => Number(s.bride?.[key]) || 0;
    const groomCount = (s, key) => Number(s.groom?.[key]) || 0;

    // Recommended set for THIS render.
    const rec = new Set();
    // Per-day rules.
    for (const s of slots) {
      if (!s.event) continue;
      // iter114 — Emit side-specific deliverables ONLY when the split is
      // active AND each side has its own crew (or, for symmetry, at least
      // one side has crew — the other side gets a placeholder so the
      // deliverable count matches the physical parallel-shoot workflow).
      if (s.split) {
        if (brideCount(s, "cinematographer") >= 1) rec.add(`Bride Side ${s.event} FF`);
        if (groomCount(s, "cinematographer") >= 1) rec.add(`Groom Side ${s.event} FF`);
        if (brideCount(s, "candid") >= 1 || brideCount(s, "traditional") >= 1) rec.add(`Bride Side ${s.event} All Photos`);
        if (groomCount(s, "candid") >= 1 || groomCount(s, "traditional") >= 1) rec.add(`Groom Side ${s.event} All Photos`);
      } else {
        if (countRole(s, "cinematographer") >= 1) rec.add(`${s.event} FF`);
        // iter45 — Candid OR Traditional ≥ 1 triggers the per-day All Photos.
        if (countRole(s, "candid") >= 1 || countRole(s, "traditional") >= 1) {
          rec.add(`${s.event} All Photos`);
        }
      }
    }
    // Global rule — total cinematographers ≥ 2 → Teaser + Trailer.
    const totalCine = slots.reduce((sum, s) => sum + countRole(s, "cinematographer"), 0);
    if (totalCine >= 2) {
      rec.add("Teaser Video");
      rec.add("Trailer Video");
    }
    // iter45 — Global "Best Photos": exactly ONE entry if ANY photographer
    // (candid or traditional) is present anywhere in the booking. Never
    // repeated per-day even if the wedding spans 5+ days.
    const totalPhotogs = slots.reduce(
      (sum, s) => sum + countRole(s, "candid") + countRole(s, "traditional"),
      0,
    );
    if (totalPhotogs >= 1) rec.add("Best Photos");

    // Only append items that are NEWLY recommended this render — i.e., were
    // not in the previous recommended set. This preserves user X-removals.
    const prev = lastRecommendedRef.current;
    const toAdd = [];
    for (const item of rec) {
      if (!prev.has(item) && !(form.deliverables || []).includes(item)) {
        toAdd.push(item);
      }
    }
    if (toAdd.length) {
      setForm((f) => ({ ...f, deliverables: [...(f.deliverables || []), ...toAdd] }));
    }
    lastRecommendedRef.current = rec;
  }, [
    showForm,
    form.day1, form.day2, form.day3, form.day4, form.day5,
    form.day_event_types, form.day_splits,
    form.crew_requirements, form.day_bride_crew, form.day_groom_crew,
    form.extra_days,
  ]);

  const filtered = useMemo(() => {
    // iter161 — "Active" hides past-date, COMPLETED, CANCEL, INQUIRY.
    // "Past" surfaces COMPLETED + CANCEL + past-date bookings so they
    // remain searchable/openable without being deleted or archived.
    const todayIso = new Date().toISOString().slice(0, 10);
    return items.filter((b) => {
      if (b.status === "INQUIRY") return activeTab === "inquiries";
      if (activeTab === "inquiries") return false;
      const isCompletedOrCancelled = b.status === "COMPLETED" || b.status === "CANCEL";
      const latest = latestBookingDate(b);
      const isPastDate = !!latest && latest < todayIso;
      const isPast = isCompletedOrCancelled || isPastDate;
      if (activeTab === "past") {
        if (!isPast) return false;
      } else {
        // "active"
        if (isPast) return false;
      }
      if (statusFilter !== "ALL" && b.status !== statusFilter) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return [b.client_name, b.couple_name, b.location, b.event, b.reference, b.contact]
        .filter(Boolean)
        .some((x) => String(x).toLowerCase().includes(s));
    });
  }, [items, q, statusFilter, activeTab]);

  // iter161 — Count of bookings that fall into the "Past / Completed"
  // bucket, shown as a badge on the tab so operators know history is there.
  const pastCount = useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    return items.filter((b) => {
      if (b.status === "INQUIRY") return false;
      if (b.status === "COMPLETED" || b.status === "CANCEL") return true;
      const latest = latestBookingDate(b);
      return !!latest && latest < todayIso;
    }).length;
  }, [items]);

  const inquiryCount = useMemo(
    () => items.filter((b) => b.status === "INQUIRY").length,
    [items]
  );

  const openCreate = () => {
    setEditing(null);
    // iter143 — check for a saved draft first
    const existing = loadDraft(draftScope, "new");
    if (existing?.value) {
      setPendingRestore(existing);
      setForm(emptyForm);
    } else {
      setForm(emptyForm);
    }
    setDirty(false);
    setDraftSavedAt(null);
    setErr("");
    setShowForm(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({ ...emptyForm, ...b });
    // iter143 — offer to restore if we have a newer draft
    const existing = loadDraft(draftScope, b.id);
    if (existing?.value && existing.savedAt > (new Date(b.updated_at || 0).getTime() || 0)) {
      setPendingRestore(existing);
    }
    setDirty(false);
    setDraftSavedAt(null);
    setErr("");
    setShowForm(true);
  };

  // iter143 — Mark form dirty whenever the user makes a real change.
  // Wraps every top-level setForm call in the modal via a proxy hook,
  // but for simplicity we watch the `form` object with a ref+useEffect.
  const firstRenderRef = useRef(true);
  useEffect(() => {
    if (!showForm) { firstRenderRef.current = true; return; }
    if (firstRenderRef.current) { firstRenderRef.current = false; return; }
    setDirty(true);
  }, [form, showForm]);

  const requestClose = () => {
    if (dirty) { setConfirmClose(true); return; }
    setShowForm(false);
    setDirty(false);
  };

  const discardAndClose = () => {
    clearDraft(draftScope, draftId);
    setShowForm(false);
    setConfirmClose(false);
    setDirty(false);
  };

  const saveAsDraftAndClose = () => {
    // draft is already autosaved; just close without clearing it
    setShowForm(false);
    setConfirmClose(false);
    setDirty(false);
  };

  const applyDraftRestore = () => {
    if (pendingRestore?.value) setForm({ ...emptyForm, ...pendingRestore.value });
    setPendingRestore(null);
    // treat restored form as dirty so autosave keeps ticking
    setDirty(true);
  };

  const rejectDraftRestore = () => {
    clearDraft(draftScope, draftId);
    setPendingRestore(null);
  };

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const payload = { ...form };
      // iter30 — strip requirement entries whose date is empty so backend
      // doesn't provision phantom rows on cleared days.
      if (payload.crew_requirements) {
        const clean = {};
        for (const [k, v] of Object.entries(payload.crew_requirements)) {
          if (form[k]) clean[k] = v;
        }
        payload.crew_requirements = clean;
      }
      // iter33 — only keep day_event_types entries for days that have a date set.
      if (payload.day_event_types) {
        const cleanEvents = {};
        for (const [k, v] of Object.entries(payload.day_event_types)) {
          const val = (v || "").toString().trim();
          if (form[k] && val) cleanEvents[k] = val;
        }
        payload.day_event_types = cleanEvents;
      }
      // iter36 — strip day_sides for inactive days; default "Both Side" stays.
      if (payload.day_sides) {
        const cleanSides = {};
        for (const [k, v] of Object.entries(payload.day_sides)) {
          const val = (v || "").toString().trim();
          if (form[k] && val) cleanSides[k] = val;
        }
        payload.day_sides = cleanSides;
      }
      // iter36 — deliverables: drop empty/whitespace-only entries.
      if (Array.isArray(payload.deliverables)) {
        payload.deliverables = payload.deliverables
          .map((s) => (s || "").toString().trim())
          .filter((s) => s.length > 0);
      }
      // iter39 — day_splits + day_bride/groom_crew only for active day1..5.
      if (payload.day_splits) {
        const cleanSplits = {};
        for (const k of ["day1", "day2", "day3", "day4", "day5"]) {
          if (form[k] && form.day_splits?.[k]) cleanSplits[k] = true;
        }
        payload.day_splits = cleanSplits;
      }
      for (const key of ["day_bride_crew", "day_groom_crew"]) {
        if (payload[key]) {
          const clean = {};
          for (const [k, v] of Object.entries(payload[key])) {
            if (form[k] && form.day_splits?.[k]) clean[k] = v;
          }
          payload[key] = clean;
        }
      }
      // iter39 — extra_days: drop rows without a date, drop bride/groom
      // crew grids when the row isn't split.
      if (Array.isArray(payload.extra_days)) {
        payload.extra_days = payload.extra_days
          .filter((d) => d && (d.date || "").trim())
          .map((d) => ({
            date: d.date,
            event_type: (d.event_type || "").trim(),
            side: d.side || "Both Side",
            split: !!d.split,
            crew: d.split ? {} : (d.crew || {}),
            bride_crew: d.split ? (d.bride_crew || {}) : {},
            groom_crew: d.split ? (d.groom_crew || {}) : {},
          }));
      }
      let resp;
      if (editing) {
        resp = await api.put(`/bookings/${editing.id}`, payload);
      } else {
        resp = await api.post("/bookings", payload);
      }
      const n = resp?.data?._crew_rows_provisioned ?? 0;
      if (n > 0) {
        alert(`✅ Booking saved. Auto-generated ${n} blank crew row${n === 1 ? "" : "s"} in Shoot Crew Management.`);
      }
      setShowForm(false);
      // iter143 — clear draft on successful save
      clearDraft(draftScope, draftId);
      setDirty(false);
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (id) => {
    // iter156 — Delete is now a soft-delete: rows move to Trash where
    // they can be restored within 30 days. Backend guards financial /
    // production records — surface those blockers cleanly.
    const b = items.find((x) => x.id === id);
    const label = b ? `${b.client_name}${b.event ? " · " + b.event : ""}` : "this event";
    if (!window.confirm(`Delete Event?\n\n"${label}" will be moved to Trash and can be restored within 30 days.\n\nContinue?`)) return;
    try {
      await api.delete(`/bookings/${id}`);
      _toast.success("Event moved to Trash");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && typeof detail === "object" && detail?.message) {
        // Protected: financial/production data exists.
        const blockers = Array.isArray(detail.blockers) ? detail.blockers.join(", ") : "";
        const canForce = user?.role === "admin";
        const msg = `${detail.message}${blockers ? `\n\nBlockers: ${blockers}` : ""}${canForce ? "\n\nAs Admin, do you want to FORCE MOVE this event to Trash anyway?" : ""}`;
        if (canForce && window.confirm(msg)) {
          try {
            await api.delete(`/bookings/${id}?force=true`);
            _toast.success("Event force-moved to Trash");
            load();
          } catch (e2) {
            _toast.error(formatApiError(e2.response?.data?.detail));
          }
        } else if (!canForce) {
          alert(msg);
        }
      } else {
        alert(formatApiError(detail));
      }
    }
  };

  const duplicate = async (id) => {
    // iter156 — Duplicate copies structural fields only. Payments,
    // tracker tokens and financial history are NOT copied.
    try {
      const { data: newBooking } = await api.post(`/bookings/${id}/duplicate`);
      _toast.success(`Duplicated as "${newBooking.client_name}"`);
      load();
    } catch (e) {
      _toast.error(formatApiError(e.response?.data?.detail) || "Could not duplicate");
    }
  };

  const promote = async (b) => {
    try {
      const { data } = await api.post(`/bookings/${b.id}/promote`);
      if (data.already_existed) {
        alert(`Already linked to a Post-Production entry for ${data.couple_name}. Opening Post-Production…`);
      }
      load();
      navigate("/post-production");
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  const exportCsv = () => {
    window.open(`${API}/export/bookings`, "_blank");
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/import/bookings", fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert(`Imported ${data.inserted} bookings`);
      load();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
    e.target.value = "";
  };

  return (
    <div className="space-y-6 fade-in" data-testid="bookings-page">
      <PageHero
        eyebrow="Kahani Studio · Client Ledger"
        title="Bookings"
        subtitle="Every inquiry, confirmed shoot, and payment milestone — filtered, searchable, and one click from an invoice."
        right={
          <>
            <button className="btn btn-outline" onClick={exportCsv} data-testid="export-bookings-btn">
              <Download className="w-4 h-4" strokeWidth={1.6} /> Export
            </button>
            {canWrite && (
              <button
                className="btn btn-outline"
                onClick={() => navigate("/bookings/trash")}
                data-testid="bookings-open-trash"
                title="View deleted events"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.6} /> Trash
              </button>
            )}
            {user?.role === "admin" && (
              <button className="btn btn-outline" onClick={() => setShowImport(true)} data-testid="import-bookings-btn">
                <Upload className="w-4 h-4" strokeWidth={1.6} /> Import
              </button>
            )}
            {canWrite && (
              <button className="btn btn-primary" onClick={openCreate} data-testid="new-booking-btn">
                <Plus className="w-4 h-4" strokeWidth={1.6} /> New booking
              </button>
            )}
            {isViewer && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200" data-testid="viewer-badge">
                <Eye className="w-3.5 h-3.5" strokeWidth={1.8} /> Read-only access
              </span>
            )}
          </>
        }
      />

      {/* iter62 — Active Bookings vs Inquiries tabs */}
      <div className="flex flex-wrap gap-1 border-b border-stone-200 -mb-px" data-testid="bookings-view-tabs">
        <button
          type="button"
          onClick={() => { setActiveTab("active"); setStatusFilter("ALL"); }}
          className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            activeTab === "active" ? "text-rose-700 border-rose-700" : "text-stone-500 border-transparent hover:text-stone-800"
          }`}
          data-testid="bookings-tab-active"
        >
          <ClipboardList className="w-3.5 h-3.5" strokeWidth={1.8} /> Active Bookings
        </button>
        <button
          type="button"
          onClick={() => { setActiveTab("inquiries"); setStatusFilter("ALL"); }}
          className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            activeTab === "inquiries" ? "text-rose-700 border-rose-700" : "text-stone-500 border-transparent hover:text-stone-800"
          }`}
          data-testid="bookings-tab-inquiries"
        >
          <Inbox className="w-3.5 h-3.5" strokeWidth={1.8} /> Inquiries
          {inquiryCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-700 text-white text-[10px] font-bold" data-testid="bookings-inquiry-count">
              {inquiryCount}
            </span>
          )}
        </button>
        {/* iter161 — Past / Completed tab: history stays searchable but off Active. */}
        <button
          type="button"
          onClick={() => { setActiveTab("past"); setStatusFilter("ALL"); }}
          className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            activeTab === "past" ? "text-rose-700 border-rose-700" : "text-stone-500 border-transparent hover:text-stone-800"
          }`}
          data-testid="bookings-tab-past"
        >
          <History className="w-3.5 h-3.5" strokeWidth={1.8} /> Past / Completed
          {pastCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-stone-500 text-white text-[10px] font-bold" data-testid="bookings-past-count">
              {pastCount}
            </span>
          )}
        </button>
      </div>

      {/* Filters */}
      <div className="card p-3 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" strokeWidth={1.5} />
          <input
            data-testid="bookings-search"
            placeholder="Search client, couple, location, event…"
            className="input pl-9"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {activeTab === "active" && (
          <select className="input w-40" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="bookings-status-filter">
            <option value="ALL">All status</option>
            {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        {activeTab === "past" && (
          <select className="input w-40" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="bookings-status-filter-past">
            <option value="ALL">All status</option>
            <option value="COMPLETED">COMPLETED</option>
            <option value="CANCEL">CANCEL</option>
            <option value="CONFIRM">CONFIRM</option>
            <option value="WAITING">WAITING</option>
          </select>
        )}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th>Status</th>
                <th>Client</th>
                <th>Contact</th>
                <th>Day 1</th>
                <th>Day 2</th>
                <th>Day 3</th>
                <th>Day 4</th>
                <th>Day 5</th>
                <th>Couple</th>
                <th>Location</th>
                <th>Event</th>
                <th>Reference</th>
                <th>Updates</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={14} className="text-center text-stone-500 py-10">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={14} className="text-center text-stone-500 py-10">No bookings found</td></tr>
              ) : (
                filtered.map((b) => (
                  <tr key={b.id} data-testid={`booking-row-${b.id}`}>
                    <td><span className={statusBadgeClass(b.status)}>{b.status}</span></td>
                    <td className="font-medium text-stone-900">{b.client_name}</td>
                    <td className="font-mono text-xs">{b.contact || "—"}</td>
                    <td className="font-mono text-xs">{fmtDate(b.day1)}</td>
                    <td className="font-mono text-xs">{fmtDate(b.day2)}</td>
                    <td className="font-mono text-xs">{fmtDate(b.day3)}</td>
                    <td className="font-mono text-xs">{fmtDate(b.day4)}</td>
                    <td className="font-mono text-xs">{fmtDate(b.day5)}</td>
                    <td>{b.couple_name || "—"}</td>
                    <td className="text-stone-600">{b.location || "—"}</td>
                    <td>{b.event || "—"}</td>
                    <td className="text-stone-600">{b.reference || "—"}</td>
                    <td className="text-stone-600 max-w-[180px] truncate" title={b.last_updates}>{b.last_updates || "—"}</td>
                    <td className="text-right whitespace-nowrap">
                      {b.contact && (
                        <a
                          href={waLink(b.contact, `Hi ${b.client_name}, reaching out regarding your booking.`)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-green-700 hover:bg-green-50"
                          title="WhatsApp"
                          data-testid={`booking-wa-${b.id}`}
                        >
                          <MessageCircle className="w-4 h-4" strokeWidth={1.6} />
                        </a>
                      )}
                      {canWrite && (
                        <button onClick={() => openEdit(b)} className="inline-flex items-center justify-center w-7 h-7 rounded-md text-stone-700 hover:bg-stone-100" title="Edit" data-testid={`booking-edit-${b.id}`}>
                          <Pencil className="w-4 h-4" strokeWidth={1.6} />
                        </button>
                      )}
                      {canWrite && (
                        <button onClick={() => setInvoiceFor(b)} className="inline-flex items-center justify-center w-7 h-7 rounded-md text-rose-700 hover:bg-rose-50" title="Generate invoice" data-testid={`booking-invoice-${b.id}`}>
                          <Receipt className="w-4 h-4" strokeWidth={1.6} />
                        </button>
                      )}
                      {b.status === "COMPLETED" && (user?.role === "admin" || user?.role === "manager") && (
                        <button
                          onClick={() => setFinalCrewFor(b)}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-emerald-700 hover:bg-emerald-50"
                          title="Edit Final Crew (post-shoot correction)"
                          data-testid={`booking-edit-final-crew-${b.id}`}
                        >
                          <Users2 className="w-4 h-4" strokeWidth={1.6} />
                        </button>
                      )}
                      {canWrite && (
                        <ProposalStatusButton
                          booking={b}
                          onOpen={() => setQuoteFor(b)}
                        />
                      )}
                      {/* iter148 — Financial Center: single-source-of-truth
                          contract amount editor. Only surfaces when a
                          proposal already exists (nothing to override
                          otherwise). Admin-only. */}
                      {canWrite && user?.role === "admin" && b.quote?.grand_total > 0 && (
                        <button
                          onClick={() => setFinancialFor(b)}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-emerald-800 hover:bg-emerald-50 kb-tap"
                          title="Financial Center — edit contract amount"
                          data-testid={`booking-financial-${b.id}`}
                          aria-label="Open Financial Center"
                        >
                          <IndianRupee className="w-4 h-4" strokeWidth={1.8} aria-hidden="true" />
                        </button>
                      )}
                      {/* iter98 — Share live tracker via WhatsApp / copy link */}
                      {canWrite && (
                        <BookingTrackerButton
                          clientName={b.client_name}
                          place={b.location}
                          whatsapp={b.contact}
                          testId={`booking-tracker-${b.id}`}
                        />
                      )}
                      {canWrite && b.status === "CONFIRM" && (
                        b.pp_id ? (
                          <button
                            onClick={() => navigate("/post-production")}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-green-700 hover:bg-green-50"
                            title="Linked to Post-Production"
                            data-testid={`booking-pp-linked-${b.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4" strokeWidth={1.6} />
                          </button>
                        ) : (
                          <button
                            onClick={() => promote(b)}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-amber-700 hover:bg-amber-50"
                            title="Move to Post-Production"
                            data-testid={`booking-promote-${b.id}`}
                          >
                            <Scissors className="w-4 h-4" strokeWidth={1.6} />
                          </button>
                        )
                      )}
                      {user?.role === "admin" && (
                        <BookingRowMenu
                          booking={b}
                          canWrite={canWrite}
                          isAdmin={user?.role === "admin"}
                          onEdit={openEdit}
                          onDuplicate={duplicate}
                          onDelete={del}
                        />
                      )}
                      {user?.role !== "admin" && canWrite && (
                        <BookingRowMenu
                          booking={b}
                          canWrite={canWrite}
                          isAdmin={false}
                          onEdit={openEdit}
                          onDuplicate={duplicate}
                          onDelete={del}
                        />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drawer / Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/30 fade-in" onClick={requestClose}>
          <div className="bg-white w-full max-w-xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 h-16 border-b border-stone-200">
              <div className="flex items-center gap-3">
                <h2 className="font-heading text-lg font-semibold">{editing ? "Edit booking" : "New booking"}</h2>
                {/* iter143 — draft-saved chip */}
                {(dirty || draftSavedAt) && (
                  <span
                    className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${dirty && !draftSavedAt ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}
                    data-testid="booking-draft-status"
                  >
                    {dirty && !draftSavedAt
                      ? "Unsaved"
                      : `Draft saved · ${fmtDraftAgo(draftSavedAt)}`}
                  </span>
                )}
              </div>
              <button className="btn-ghost p-2 rounded" onClick={requestClose} data-testid="booking-form-close">
                <X className="w-5 h-5" strokeWidth={1.6} />
              </button>
            </div>
            {/* iter143 — Restore-draft banner */}
            {pendingRestore && (
              <div className="mx-6 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex items-center justify-between gap-3" data-testid="booking-draft-restore-banner">
                <div className="text-[13px] text-amber-900">
                  We found an unsaved draft from <b>{fmtDraftAgo(pendingRestore.savedAt)}</b>. Restore it?
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button type="button" className="btn btn-outline btn-sm" onClick={rejectDraftRestore} data-testid="booking-draft-reject">Discard</button>
                  <button type="button" className="btn btn-primary btn-sm" onClick={applyDraftRestore} data-testid="booking-draft-restore">Restore draft</button>
                </div>
              </div>
            )}
            <form onSubmit={save} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Status</label>
                  <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="booking-form-status">
                    {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Reference</label>
                  <input className="input" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="INSTA, Friend, etc." data-testid="booking-form-reference" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Client name *</label>
                  <input required className="input" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="booking-form-client" />
                </div>
                <div>
                  <label className="label">Contact</label>
                  <input className="input" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="10-digit number" data-testid="booking-form-contact" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Couple name</label>
                  <input className="input" value={form.couple_name} onChange={(e) => setForm({ ...form, couple_name: e.target.value })} data-testid="booking-form-couple" />
                </div>
                <div>
                  <label className="label">Event</label>
                  <input className="input" value={form.event} onChange={(e) => setForm({ ...form, event: e.target.value })} placeholder="Wedding, Engagement…" data-testid="booking-form-event" />
                </div>
              </div>
              <div>
                <label className="label">Location</label>
                <input className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="booking-form-location" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Event days + per-day crew requirements <span className="normal-case font-normal text-stone-400">(add as many as you need)</span></div>
                <div className="space-y-3" data-testid="booking-form-days-list">
                  {["day1", "day2", "day3", "day4", "day5"].map((k, i) => (
                    <DayBlock
                      key={k}
                      testKey={k}
                      label={`Day ${i + 1}`}
                      value={{
                        date: form[k] || "",
                        event_type: form.day_event_types?.[k] || "",
                        side: form.day_sides?.[k] || "Both Side",
                        split: !!form.day_splits?.[k],
                        crew: form.crew_requirements?.[k] || {},
                        bride_crew: form.day_bride_crew?.[k] || {},
                        groom_crew: form.day_groom_crew?.[k] || {},
                      }}
                      onChange={(patch) => {
                        setForm((prev) => ({
                          ...prev,
                          ...(patch.date !== undefined ? { [k]: patch.date } : {}),
                          day_event_types: patch.event_type !== undefined
                            ? { ...(prev.day_event_types || {}), [k]: patch.event_type }
                            : prev.day_event_types,
                          day_sides: patch.side !== undefined
                            ? { ...(prev.day_sides || {}), [k]: patch.side }
                            : prev.day_sides,
                          day_splits: patch.split !== undefined
                            ? { ...(prev.day_splits || {}), [k]: patch.split }
                            : prev.day_splits,
                          crew_requirements: patch.crew !== undefined
                            ? { ...(prev.crew_requirements || {}), [k]: patch.crew }
                            : prev.crew_requirements,
                          day_bride_crew: patch.bride_crew !== undefined
                            ? { ...(prev.day_bride_crew || {}), [k]: patch.bride_crew }
                            : prev.day_bride_crew,
                          day_groom_crew: patch.groom_crew !== undefined
                            ? { ...(prev.day_groom_crew || {}), [k]: patch.groom_crew }
                            : prev.day_groom_crew,
                        }));
                      }}
                    />
                  ))}
                  {(form.extra_days || []).map((row, idx) => (
                    <DayBlock
                      key={`extra-${idx}`}
                      testKey={`extra${idx}`}
                      label={`Day ${6 + idx}`}
                      value={row}
                      onRemove={() => setForm((prev) => ({
                        ...prev,
                        extra_days: prev.extra_days.filter((_, i) => i !== idx),
                      }))}
                      onChange={(patch) => setForm((prev) => ({
                        ...prev,
                        extra_days: prev.extra_days.map((d, i) => i === idx ? { ...d, ...patch } : d),
                      }))}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setForm((prev) => ({
                    ...prev,
                    extra_days: [
                      ...(prev.extra_days || []),
                      { date: "", event_type: "", side: "Both Side", split: false, crew: {}, bride_crew: {}, groom_crew: {} },
                    ],
                  }))}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-rose-800 bg-rose-50 hover:bg-rose-100 border border-dashed border-rose-300 rounded px-3 py-1.5 transition"
                  data-testid="booking-form-add-day"
                >
                  <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
                  Add New Day
                </button>
                {editing?.crew_provisioned && (
                  <div className="mt-2 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1" data-testid="booking-form-crew-provisioned-flag">
                    ✅ Crew already provisioned on {editing.crew_provisioned_at?.slice(0, 10)}. Further status changes will not create duplicates.
                  </div>
                )}
                {form.status === "CONFIRM" && !editing?.crew_provisioned && Object.values(form.crew_requirements || {}).some((r) => Object.values(r).some((v) => Number(v) > 0)) && (
                  <div className="mt-2 text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1" data-testid="booking-form-provision-hint">
                    On <b>Update</b> the {Object.entries(form.crew_requirements).reduce((s, [, v]) => s + Object.values(v).reduce((a, b) => a + (Number(b) || 0), 0), 0)} slot(s) below will auto-populate Shoot Crew Management.
                  </div>
                )}
              </div>
              {/* iter36 — Deliverables list builder */}
              <div data-testid="booking-form-deliverables-section">
                <div className="flex items-center justify-between mb-2">
                  <label className="label mb-0">📦 Deliverables (invoice PDF)</label>
                  <DeliverablesPicker
                    value={form.deliverables || []}
                    onChange={(next) => setForm({ ...form, deliverables: next })}
                    testid="booking-form-deliverables-picker"
                  />
                </div>
                <div className="space-y-1.5">
                  {(form.deliverables || []).length === 0 && (
                    <div className="text-[11px] text-stone-400 italic" data-testid="booking-form-deliverables-empty">
                      No deliverables yet. Click <b>+ Add item</b> to list what&apos;s included (e.g., &quot;1× Premium Leather Album&quot;).
                    </div>
                  )}
                  {(form.deliverables || []).map((item, idx) => (
                    <div key={`${item}-${idx}`} className="flex items-center gap-2" data-testid={`booking-form-deliverable-row-${idx}`}>
                      <span className="text-stone-400 text-xs w-4 text-right">•</span>
                      <input
                        type="text"
                        className="input py-1 text-sm flex-1"
                        placeholder="e.g., 1× Premium Leather Album"
                        value={item}
                        onChange={(e) => {
                          const next = [...(form.deliverables || [])];
                          next[idx] = e.target.value;
                          setForm({ ...form, deliverables: next });
                        }}
                        data-testid={`booking-form-deliverable-input-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const next = [...(form.deliverables || [])];
                          next.splice(idx, 1);
                          setForm({ ...form, deliverables: next });
                        }}
                        className="p-1 rounded hover:bg-red-50 text-stone-400 hover:text-red-700 shrink-0"
                        title="Remove"
                        data-testid={`booking-form-deliverable-remove-${idx}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label className="label">Last updates / notes</label>
                <textarea className="input" rows={2} value={form.last_updates} onChange={(e) => setForm({ ...form, last_updates: e.target.value })} data-testid="booking-form-updates" />
              </div>
              <div>
                <label className="label">Management</label>
                <input className="input" value={form.management} onChange={(e) => setForm({ ...form, management: e.target.value })} data-testid="booking-form-management" />
              </div>

              {/* iter137 — Split Payment: multi-party responsibility */}
              <SplitPaymentSection
                booking={editing}
                value={form.payment_split}
                onChange={(next) => setForm({ ...form, payment_split: next })}
                onPersist={async (next) => {
                  if (!editing?.id) return;
                  try {
                    const { data } = await api.patch(`/bookings/${editing.id}/payment-split`, next);
                    setForm((f) => ({ ...f, payment_split: data.payment_split }));
                    _toast.success("Split payment saved");
                    load();
                  } catch (e) {
                    _toast.error(formatApiError(e.response?.data?.detail) || e.message);
                  }
                }}
              />

              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="btn btn-outline" onClick={requestClose} data-testid="booking-form-cancel">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="booking-form-save">
                  <Save className="w-4 h-4" strokeWidth={1.6} /> {editing ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* iter143 — Unsaved-changes confirm modal */}
      {confirmClose && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
          data-testid="booking-unsaved-modal"
          onClick={() => setConfirmClose(false)}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-lg text-stone-900">Unsaved changes</h3>
            <p className="text-sm text-stone-600 mt-2">
              You have unsaved edits on this booking. What would you like to do?
            </p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button className="btn btn-outline btn-sm" onClick={() => setConfirmClose(false)} data-testid="unsaved-continue">
                Continue Editing
              </button>
              <button className="btn btn-outline btn-sm" onClick={saveAsDraftAndClose} data-testid="unsaved-save-draft">
                Save as Draft
              </button>
              <button className="btn btn-primary btn-sm bg-rose-700 hover:bg-rose-800" onClick={discardAndClose} data-testid="unsaved-discard">
                Discard Changes
              </button>
            </div>
            <p className="text-[11px] text-stone-500 mt-3 italic">
              Drafts are stored locally on this device for 7 days.
            </p>
          </div>
        </div>
      )}
      {showImport && (
        <ImportMapper
          kind="bookings"
          open={showImport}
          onClose={() => setShowImport(false)}
          onDone={load}
        />
      )}
      {invoiceFor && (
        <InvoiceModal
          open={!!invoiceFor}
          booking={invoiceFor}
          onClose={() => setInvoiceFor(null)}
        />
      )}
      <ProposalModal
        open={!!quoteFor}
        booking={quoteFor}
        onClose={() => setQuoteFor(null)}
      />
      <FinancialCenterModal
        open={!!financialFor}
        booking={financialFor}
        onClose={() => setFinancialFor(null)}
        onSaved={() => { setFinancialFor(null); load(); }}
      />
      {finalCrewFor && (
        <EditFinalCrewDrawer
          booking={finalCrewFor}
          onClose={() => setFinalCrewFor(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}


// iter39 — Single event-day row. Handles both fixed day1..5 slots and the
// unbounded extra_days entries via a normalised {date, event_type, side,
// split, crew, bride_crew, groom_crew} value shape + `onChange(patch)`.
function DayBlock({ testKey, label, value, onChange, onRemove }) {
  const active = !!(value?.date || "").trim();
  const split = !!value?.split;

  const crewTotal = (bucket) => CREW_ROLES.reduce((s, r) => s + (Number(bucket?.[r.key]) || 0), 0);
  const totalCrew = split ? (crewTotal(value.bride_crew) + crewTotal(value.groom_crew)) : crewTotal(value.crew);
  const totalCost = split
    ? CREW_ROLES.reduce((s, r) => s + ((Number(value.bride_crew?.[r.key]) || 0) + (Number(value.groom_crew?.[r.key]) || 0)) * r.rate, 0)
    : CREW_ROLES.reduce((s, r) => s + (Number(value.crew?.[r.key]) || 0) * r.rate, 0);

  const patchCrewBucket = (bucketKey, roleKey, n) => {
    const bucket = { ...(value[bucketKey] || {}), [roleKey]: n };
    onChange({ [bucketKey]: bucket });
  };

  return (
    <div
      className={`border rounded-lg p-3 ${active ? "border-rose-200 bg-rose-50/40" : "border-stone-200 bg-stone-50/40"}`}
      data-testid={`booking-form-day-block-${testKey}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <label className="label mb-0 shrink-0">{label}</label>
        <input
          type="date"
          className="input py-1 text-sm w-44"
          value={value.date || ""}
          onChange={(e) => onChange({ date: e.target.value })}
          data-testid={`booking-form-${testKey}`}
        />
        {active && (
          <>
            <input
              type="text"
              list={`booking-event-presets-${testKey}`}
              className="input py-1 text-sm w-40"
              placeholder="Event type (e.g., Haldi)"
              value={value.event_type || ""}
              onChange={(e) => onChange({ event_type: e.target.value })}
              data-testid={`booking-form-${testKey}-event-type`}
            />
            <datalist id={`booking-event-presets-${testKey}`}>
              {EVENT_TYPE_PRESETS.map((p) => (<option key={p} value={p} />))}
            </datalist>
            {!split && (
              <select
                className="input py-1 text-sm w-32"
                value={value.side || "Both Side"}
                onChange={(e) => onChange({ side: e.target.value })}
                data-testid={`booking-form-${testKey}-side`}
              >
                {SIDE_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
            )}
            <span className="ml-auto text-[10px] uppercase tracking-wider text-stone-500 font-semibold" data-testid={`booking-form-${testKey}-crew-total`}>
              {/* iter113 — Crew rates removed from Booking modal. Show
                   only the headcount so admins never see per-day rates
                   in this surface. */}
              {totalCrew} crew
            </span>
            {onRemove && (
              <button
                type="button"
                onClick={onRemove}
                className="ml-1 p-1 rounded hover:bg-red-50 text-stone-400 hover:text-red-700"
                title="Remove this day"
                data-testid={`booking-form-${testKey}-remove`}
              >
                <X className="w-3.5 h-3.5" strokeWidth={2.2} />
              </button>
            )}
          </>
        )}
      </div>

      {active && (
        <label className="flex items-center gap-2 mt-2 text-[11px] text-stone-600 select-none cursor-pointer" data-testid={`booking-form-${testKey}-split-toggle-label`}>
          <span
            className={`relative inline-block w-8 h-4 rounded-full transition ${split ? "bg-rose-500" : "bg-stone-300"}`}
            aria-hidden
          >
            <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${split ? "left-4" : "left-0.5"}`} />
          </span>
          <input
            type="checkbox"
            className="sr-only"
            checked={split}
            onChange={(e) => onChange({ split: e.target.checked })}
            data-testid={`booking-form-${testKey}-split-toggle`}
          />
          <span className="uppercase tracking-wider font-semibold">Split crew for parallel sides?</span>
          {split && <span className="text-[9px] text-rose-700 italic">Bride + Groom lanes below</span>}
        </label>
      )}

      {active && !split && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-2 mt-3" data-testid={`booking-form-${testKey}-crew-matrix`}>
          {CREW_ROLES.map((role) => (
            <CrewCountInput
              key={role.key}
              role={role}
              testKey={`booking-form-${testKey}-${role.key}`}
              value={value.crew?.[role.key] ?? 0}
              onChange={(n) => patchCrewBucket("crew", role.key, n)}
            />
          ))}
        </div>
      )}

      {active && split && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <SplitLane
            title="👰 Bride Side Crew"
            testKey={`booking-form-${testKey}-bride`}
            crew={value.bride_crew}
            onCrewChange={(role, n) => patchCrewBucket("bride_crew", role, n)}
          />
          <SplitLane
            title="🤵 Groom Side Crew"
            testKey={`booking-form-${testKey}-groom`}
            crew={value.groom_crew}
            onCrewChange={(role, n) => patchCrewBucket("groom_crew", role, n)}
          />
        </div>
      )}
    </div>
  );
}

function SplitLane({ title, testKey, crew, onCrewChange }) {
  const laneTotal = CREW_ROLES.reduce((s, r) => s + (Number(crew?.[r.key]) || 0), 0);
  return (
    <div className="border border-rose-100 bg-white rounded-md p-2" data-testid={testKey}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wider text-rose-800 font-bold">{title}</span>
        <span className="text-[10px] text-stone-500 font-mono">{laneTotal} crew</span>
      </div>
      {/* iter202 — 2 columns so 8 role labels never break mid-word; the native spinners are stripped
          via .crew-num so 2-digit numbers stay visible in 40-50px cells. */}
      <div className="grid grid-cols-2 gap-x-1.5 gap-y-1">
        {CREW_ROLES.map((role) => (
          <CrewCountInput
            key={role.key}
            role={role}
            testKey={`${testKey}-${role.key}`}
            value={crew?.[role.key] ?? 0}
            onChange={(n) => onCrewChange(role.key, n)}
          />
        ))}
      </div>
    </div>
  );
}

function CrewCountInput({ role, value, onChange, testKey }) {
  return (
    <div className="min-w-0 flex flex-col">
      <span
        className="text-[9px] uppercase tracking-wider text-stone-600 font-bold leading-snug break-words mb-1"
      >
        {role.label}
      </span>
      {/* iter113 — Per-day rate hint removed. Rates are staff-payroll data,
           not booking-modal data — they belong in the Team → Roster view. */}
      <input
        type="number"
        inputMode="numeric"
        min="0"
        max="10"
        step="1"
        className="crew-num"
        value={value}
        onChange={(e) => onChange(Math.max(0, Math.min(10, Number(e.target.value) || 0)))}
        data-testid={testKey}
      />
    </div>
  );
}

// iter137 — Split Payment editor.
// Renders inside the booking modal, right below the Management field.
// Enable → default 2 parties at 50/50 (Bride Side, Groom Side).
// Each party captures Name, Amount, Phone, Email, Relationship, Notes.
// Live-computed balance chip turns rose when the parties' sum ≠ grand
// total. "Save split" persists via PATCH so the tracker tokens get
// minted server-side and the parties can be shared right away.
function SplitPaymentSection({ booking, value, onChange, onPersist }) {
  const grandTotal = Number(booking?.quote?.grand_total || 0);
  const enabled = !!value?.enabled;
  const parties = value?.parties || [];
  const sum = parties.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const balanced = Math.abs(sum - grandTotal) <= 1;
  const missing = Math.max(0, grandTotal - sum);
  const extra = Math.max(0, sum - grandTotal);

  const setEnabled = (next) => {
    if (next && parties.length === 0 && grandTotal > 0) {
      const half = Math.round(grandTotal / 2);
      onChange({
        enabled: true,
        parties: [
          { id: cryptoRandomId(), name: "Bride Side", amount: half, phone: "", email: "", relationship: "", notes: "" },
          { id: cryptoRandomId(), name: "Groom Side", amount: grandTotal - half, phone: "", email: "", relationship: "", notes: "" },
        ],
      });
    } else {
      onChange({ ...(value || {}), enabled: next });
    }
  };

  const updateParty = (idx, patch) => {
    const next = parties.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    onChange({ ...(value || {}), parties: next });
  };

  const addParty = () => {
    const remaining = Math.max(0, grandTotal - sum);
    onChange({
      ...(value || {}),
      enabled: true,
      parties: [
        ...parties,
        { id: cryptoRandomId(), name: `Party ${parties.length + 1}`, amount: remaining, phone: "", email: "", relationship: "", notes: "" },
      ],
    });
  };

  const removeParty = (idx) => {
    const next = parties.filter((_, i) => i !== idx);
    onChange({ ...(value || {}), parties: next });
  };

  const splitEvenly = () => {
    if (parties.length === 0 || grandTotal <= 0) return;
    const each = Math.floor(grandTotal / parties.length);
    const remainder = grandTotal - each * parties.length;
    onChange({
      ...(value || {}),
      parties: parties.map((p, i) => ({ ...p, amount: each + (i === 0 ? remainder : 0) })),
    });
  };

  return (
    <div
      className="border border-amber-200 bg-amber-50/40 rounded-lg p-4"
      data-testid="booking-form-split-section"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="label mb-0 flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 accent-amber-700"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              data-testid="split-enabled-toggle"
            />
            <span className="font-semibold text-amber-900">Split Payment across parties</span>
          </label>
        </div>
        {grandTotal > 0 ? (
          <div className="text-xs text-stone-600">
            Grand Total: <span className="font-mono font-semibold text-stone-900">₹{grandTotal.toLocaleString("en-IN")}</span>
          </div>
        ) : (
          <div className="text-xs text-rose-700">
            Create a proposal first to enable Split Payment.
          </div>
        )}
      </div>

      {enabled && grandTotal > 0 && (
        <div className="mt-4 space-y-3" data-testid="split-parties-container">
          {parties.map((p, idx) => (
            <div
              key={p.id || idx}
              className="bg-white rounded-md border border-stone-200 p-3 space-y-2"
              data-testid={`split-party-row-${idx}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="text"
                  className="input py-1 text-sm flex-1 min-w-[140px]"
                  placeholder="Party name (e.g., Bride Side)"
                  value={p.name || ""}
                  onChange={(e) => updateParty(idx, { name: e.target.value })}
                  data-testid={`split-party-name-${idx}`}
                />
                <input
                  type="number"
                  className="input py-1 text-sm w-36 font-mono"
                  placeholder="Amount"
                  value={p.amount || ""}
                  onChange={(e) => updateParty(idx, { amount: Number(e.target.value) || 0 })}
                  data-testid={`split-party-amount-${idx}`}
                />
                <button
                  type="button"
                  className="p-1.5 rounded hover:bg-rose-50 text-stone-500 hover:text-rose-700"
                  onClick={() => removeParty(idx)}
                  title="Remove party"
                  data-testid={`split-party-remove-${idx}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input
                  type="tel"
                  className="input py-1 text-sm"
                  placeholder="Phone (WhatsApp)"
                  value={p.phone || ""}
                  onChange={(e) => updateParty(idx, { phone: e.target.value })}
                  data-testid={`split-party-phone-${idx}`}
                />
                <input
                  type="email"
                  className="input py-1 text-sm"
                  placeholder="Email"
                  value={p.email || ""}
                  onChange={(e) => updateParty(idx, { email: e.target.value })}
                  data-testid={`split-party-email-${idx}`}
                />
                <input
                  type="text"
                  className="input py-1 text-sm"
                  placeholder="Relationship (e.g., Bride's Father)"
                  value={p.relationship || ""}
                  onChange={(e) => updateParty(idx, { relationship: e.target.value })}
                  data-testid={`split-party-relationship-${idx}`}
                />
              </div>
              {p.tracker_token && booking?.id && (
                <div className="text-[11px] text-stone-500 flex items-center gap-2 flex-wrap">
                  <span>Tracker:</span>
                  <code className="font-mono bg-stone-100 px-1.5 py-0.5 rounded text-stone-700 truncate max-w-[220px]">/track/split/{p.tracker_token}</code>
                  <button
                    type="button"
                    className="text-amber-700 hover:text-amber-900 underline"
                    onClick={() => {
                      const url = `${window.location.origin}/track/split/${p.tracker_token}`;
                      navigator.clipboard?.writeText(url).catch(() => {});
                      _toast.success(`Copied ${p.name}'s tracker link`);
                    }}
                    data-testid={`split-party-copy-tracker-${idx}`}
                  >
                    Copy link
                  </button>
                  {p.phone && (
                    <a
                      href={`https://wa.me/${(p.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Hi ${p.name}, here is your private Kahani payment tracker:\n${window.location.origin}/track/split/${p.tracker_token}`)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-emerald-700 hover:text-emerald-900 underline"
                      data-testid={`split-party-wa-${idx}`}
                    >
                      Send via WhatsApp
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex gap-2">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={addParty}
                data-testid="split-add-party"
              >
                <Plus className="w-3.5 h-3.5" /> Add party
              </button>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={splitEvenly}
                data-testid="split-even"
              >
                Split evenly
              </button>
            </div>
            <div className="flex items-center gap-2">
              <div
                className={`text-xs font-mono px-2 py-1 rounded ${balanced ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}
                data-testid="split-balance-indicator"
              >
                {balanced ? "✓ Balanced" : (missing > 0 ? `Under by ₹${missing.toLocaleString("en-IN")}` : `Over by ₹${extra.toLocaleString("en-IN")}`)}
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={!balanced || !booking?.id}
                onClick={() => onPersist({ enabled: true, parties })}
                data-testid="split-save"
                title={!booking?.id ? "Save the booking first, then edit split" : ""}
              >
                Save split
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function cryptoRandomId() {
  try {
    // Browsers with crypto.randomUUID
    if (typeof window !== "undefined" && window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (_) { /* noop */ }
  return `p_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
}
